// ─────────────────────────────────────────────────────────────────────────────
// The clinic's wall clock in the doctor panel — the zone the visit room reads
// «امروز» on (CLAUDE.md «Clinic wall clock», PM-0088; PM-0618 §7.2, PM-0635(2)).
//
// The kit's order form counts «۱ ماه» / «۳ ماه» from «امروز», refuses a «تاریخ
// انجام» before it, and captions an order dated today «در همین مراجعه»; the
// service judges every one of those on the CLINIC's day. Handed no zone, the kit
// reads «امروز» on the locale's (Tehran's), so in a Baghdad clinic between
// Tehran's midnight and Baghdad's the room was a day ahead of the service.
// clinicalHost.js hands the kit `activeClinicZone`, read each time it asks.
//
// Which clinic: the panel is clinic-agnostic everywhere but پذیرش, so the zone is
// the one the doctor's clinics leave no doubt about —
//   • the clinic picked on پذیرش («پذیرش در کدام مطب؟», the kit's activeClinic),
//     when it is still one of theirs;
//   • else the zone every one of their clinics shares — a doctor with one clinic
//     has it, and so does one with several in one country;
//   • else, or while unknown, null: the kit keeps the locale's zone, as before.
//
// The zone is `Clinic.timezone` — clinicmanagement's `clinics.timezone`, the zone
// the BFF and the services read the clinic's day on. The doctor BFF serves it for
// the clinics this account OWNS (`listClinics`); a clinic the doctor only joined
// comes from `myClinics`, which carries no zone, so its zone is unknown and never
// guessed — not the country's `defaultTimezone` either, because a clinic made
// before PM-0088 reads Asia/Tehran whatever its country, and the service goes by
// the clinic's own.
//
// Asked in its OWN document, once per session, after sign-in (App.jsx): a BFF
// that refuses it costs this answer only, never a screen.
// ─────────────────────────────────────────────────────────────────────────────

import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';
import { ALL_CLINICS, readActiveClinicId } from '@parsina/framework/clinical/src/lib/activeClinic';
import { ENTERABLE } from '../hooks/useClinicSwitch';

export const CLINIC_ZONES_QUERY = `
  query DoctorClinicZones {
    myClinics { clinicId status }
    listClinics(limit: 50) { clinics { id timezone } }
  }
`;

// The doctor's clinics and the zones known for them, or null: not answered (yet).
let known = null;
let loading = null;

// The doctor's clinics are useClinicSwitch's: every clinic they own, and every
// membership that lets them in. A membership whose clinic could not be named is
// still one of them — its zone is unknown, which is all that can be said.
function toKnown(data) {
  const zones = new Map();
  for (const c of data?.listClinics?.clinics || []) {
    if (c?.id) zones.set(c.id, typeof c.timezone === 'string' ? c.timezone.trim() : '');
  }
  const ids = new Set(zones.keys());
  for (const link of data?.myClinics || []) {
    if (link?.clinicId && ENTERABLE.has(link.status)) ids.add(link.clinicId);
  }
  return { ids: [...ids], zones };
}

/**
 * Ask once. Never rejects: a failed read leaves the zone unknown (and may be
 * asked again), which is exactly how the panel behaved before it asked at all.
 */
export function loadClinicZones() {
  if (!loading) {
    loading = bffQuery(CLINIC_ZONES_QUERY)
      .then((data) => { known = toKnown(data); })
      .catch(() => { known = null; loading = null; });
  }
  return loading;
}

/** The IANA zone of the clinic the doctor is working in (see the head), or null. */
export function activeClinicZone() {
  if (!known) return null;
  const zoneOf = (id) => known.zones.get(id) || null;
  const picked = readActiveClinicId();
  if (picked && picked !== ALL_CLINICS && known.ids.includes(picked)) return zoneOf(picked);
  const zones = known.ids.map(zoneOf);
  return zones.length > 0 && zones.every((z) => z && z === zones[0]) ? zones[0] : null;
}

/** Tests only: forget the answer. */
export function resetClinicZones() {
  known = null;
  loading = null;
}
